import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { testimonials } from '../mock';
import { useLang } from '../i18n/LanguageContext';

const Testimonials = () => {
  const { t } = useLang();
  const [start, setStart] = useState(0);
  const perPage = 3;
  const maxStart = Math.max(0, testimonials.length - perPage);

  const prev = () => setStart((s) => Math.max(0, s - 1));
  const next = () => setStart((s) => Math.min(maxStart, s + 1));
  const visible = testimonials.slice(start, start + perPage);

  return (
    <section id="reviews" className="py-24 md:py-32 bg-[#0B3D2E] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-[#124D3A] border border-[#D4AF37]/25 text-xs text-[#ffffff]/85 mb-6">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            Sourced from Google Reviews
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl mb-4">
            <span className="text-[#ffffff]">{t('tm.title')} </span>
            <span className="peach-text italic">{t('tm.title.italic')}</span>
          </h2>
          <p className="text-[#ffffff]/70">{t('tm.sub')}</p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((t, i) => (
              <article
                key={`${t.name}-${i}`}
                className="bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-8 card-hover flex flex-col relative overflow-hidden"
              >
                <Quote className="absolute top-6 right-6 w-12 h-12 text-[#D4AF37]/10" fill="currentColor" />

                <div className="flex items-center gap-4 mb-5">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]/30"
                  />
                  <div>
                    <div className="font-medium text-[#ffffff] flex items-center gap-2">
                      {t.name}
                      <span className="text-base">{t.flag}</span>
                    </div>
                    <div className="text-xs text-[#ffffff]/60 mt-0.5">{t.country} · {t.treatment}</div>
                  </div>
                </div>

                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.rating)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                </div>

                <p className="text-[#ffffff]/85 leading-relaxed text-[15px] italic grow">
                  “{t.text}”
                </p>

                <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#D4AF37]/10">
                  <div className="flex items-center gap-2 text-xs text-[#ffffff]/55">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
                    </svg>
                    Posted on Google
                  </div>
                  <div className="text-xs text-[#ffffff]/55">{t.date}</div>
                </div>
              </article>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-10">
            <button
              onClick={prev}
              disabled={start === 0}
              className="w-12 h-12 rounded-full bg-[#124D3A] border border-[#D4AF37]/30 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors text-[#ffffff] disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            {[...Array(maxStart + 1)].map((_, i) => (
              <button
                key={i}
                onClick={() => setStart(i)}
                className={`h-2 rounded-full transition-all ${start === i ? 'w-8 bg-[#D4AF37]' : 'w-2 bg-[#D4AF37]/30'}`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
            <button
              onClick={next}
              disabled={start === maxStart}
              className="w-12 h-12 rounded-full bg-[#D4AF37] text-[#0B3D2E] flex items-center justify-center hover:bg-[#C49B2C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Google rating pill */}
        <div className="flex justify-center mt-14">
          <a
            href="https://www.google.com/search?q=GOLD+CARE+CLINIC"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 bg-[#f5ede2] text-[#0B3D2E] rounded-full px-6 py-4 shadow-xl hover:shadow-2xl transition-shadow"
          >
            <div className="flex items-center gap-1.5">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
              </svg>
              <span className="text-sm font-medium">Google Reviews</span>
            </div>
            <div className="w-px h-6 bg-[#0B3D2E]/20" />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
              ))}
              <span className="ml-2 font-semibold">5.0/5</span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
